import { useEffect, useState } from "react";
import { Check, TableProperties, Undo2 } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const Pill = ({ score, meta }) => {
  const m = meta[score];
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-[11px] font-bold leading-tight ${m.cls}`}>
      {m.label}
    </span>
  );
};

// Συνολικός κώδικας ανά νησί σε αποχρώσεις του γκρι→μαύρου (χωρίς έντονα χρώματα),
// με βάση ΜΟΝΟ τα επιλεγμένα κριτήρια — ένδειξη εγγύτητας, όχι «βράβευση»
const SCORE_VALUE = { strong: 2, medium: 1, weak: 0 };
const RANK_STYLES = [
  { cls: "bg-ink", name: "text-paper", sub: "text-paper/80", dot: "bg-ink", labelKey: "boardRankBest" },
  { cls: "bg-neutral-500", name: "text-paper", sub: "text-paper/85", dot: "bg-neutral-500", labelKey: "boardRankMid" },
  { cls: "bg-neutral-300", name: "text-ink", sub: "text-ink/70", dot: "bg-neutral-300", labelKey: "boardRankWeak" },
];

// Διαδραστικός πίνακας αξιολόγησης θεωριών:
// Βήμα 1 — ο χρήστης επιλέγει ποια ομηρικά κριτήρια θεωρεί ισχυρά.
// Βήμα 2 — σχηματίζεται ο πίνακας αξιολόγησης μόνο για τα επιλεγμένα κριτήρια (χωρίς συμπέρασμα).
export const CriteriaBoardPage = () => {
  const { d, ui } = useLang();
  const { CRITERIA, THEORIES, SCORE_META } = d;
  // Η τοπωνυμική συνέχεια είναι πάντα επιλεγμένη (κλειδωμένη)
  const LOCKED_INDEX = CRITERIA.findIndex((c) => /Τοπωνυμική|Toponymic/i.test(c.criterion));
  const [picked, setPicked] = useState(() => new Set(LOCKED_INDEX >= 0 ? [LOCKED_INDEX] : []));
  const [built, setBuilt] = useState(false);

  // Μετά από «Σχηματισμός» ή «Αλλαγή κριτηρίων» η σελίδα επιστρέφει στην κορυφή (μεταβαίνουμε μετά το render)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [built]);

  const toggle = (i) => {
    if (i === LOCKED_INDEX) return;
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const rows = CRITERIA.filter((_, i) => picked.has(i));

  // Σύνολο ανά νησί στα επιλεγμένα κριτήρια → κατάταξη (ισοπαλίες παίρνουν το ίδιο χρώμα)
  const weightOf = (row) => (CRITERIA.indexOf(row) === LOCKED_INDEX ? 2 : 1);
  const totals = Object.fromEntries(
    THEORIES.map((t) => [t.key, rows.reduce((s, r) => s + weightOf(r) * (SCORE_VALUE[r.cells[t.key].score] ?? 0), 0)])
  );
  const maxTotal = rows.reduce((s, r) => s + weightOf(r) * 2, 0);
  const distinct = [...new Set(Object.values(totals))].sort((a, b) => b - a);
  const rankOf = (key) => Math.min(distinct.indexOf(totals[key]), 2);

  return (
    <main data-testid="board-main" className="min-h-[70vh] bg-surface/50">
      <div className="mx-auto max-w-6xl px-6 pb-14 pt-10">
        <p className="eyebrow">{ui.boardEyebrow}</p>
        <h1 data-testid="board-title" className="mt-2 font-serif text-3xl font-semibold tracking-tight text-ink md:text-5xl">
          {ui.boardTitle}
        </h1>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg">{ui.boardIntro}</p>

        {!built ? (
          <section data-testid="board-select" className="mt-10">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-serif text-xl font-semibold text-ink md:text-2xl">{ui.boardStep1}</h2>
              <p data-testid="board-count" className="rounded-full bg-aegean/10 px-4 py-1.5 text-sm font-semibold text-aegean">
                {picked.size} {ui.boardSelectedCount}
              </p>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {CRITERIA.map((c, i) => {
                const on = picked.has(i);
                return (
                  <button
                    key={c.criterion}
                    type="button"
                    data-testid={`board-criterion-${i}`}
                    aria-pressed={on}
                    aria-disabled={i === LOCKED_INDEX || undefined}
                    title={i === LOCKED_INDEX ? ui.boardLockedHint : undefined}
                    onClick={() => toggle(i)}
                    className={`relative rounded-xl border p-5 text-left transition-colors duration-150 ${
                      i === LOCKED_INDEX ? "cursor-not-allowed opacity-90 " : ""
                    }${on ? "border-aegean bg-aegean/5 shadow-sm" : "border-line bg-paper hover:border-aegean/60"}`}
                  >
                    <span
                      className={`absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full border transition-colors ${
                        on ? "border-aegean bg-aegean text-paper" : "border-line text-transparent"
                      }`}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <p className="pr-8 font-serif text-lg font-semibold text-ink">{c.criterion}</p>
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-terra">{c.source}</p>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.question}</p>
                    {i === LOCKED_INDEX && (
                      <p data-testid="board-locked-note" className="mt-3 border-t border-line pt-2 text-[11px] font-semibold text-terra">
                        {ui.boardLockedNote}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                data-testid="board-build"
                disabled={picked.size === 0}
                onClick={() => { setBuilt(true); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="inline-flex items-center gap-2 rounded-full bg-aegean px-8 py-3.5 text-sm font-semibold text-paper transition-colors duration-200 hover:bg-ink disabled:opacity-40"
              >
                <TableProperties className="h-4 w-4" />
                {ui.boardBuild}
              </button>
            </div>
          </section>
        ) : (
          <section data-testid="board-result" className="mt-10">
            <h2 className="font-serif text-xl font-semibold text-ink md:text-2xl">{ui.boardStep2}</h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">{ui.boardResultNote}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="board-legend">
              {Object.values(SCORE_META).map((m) => (
                <span key={m.label} className={`rounded-full px-3 py-1 text-[11px] font-bold ${m.cls}`}>{m.label}</span>
              ))}
            </div>

            {/* Mobile: κάρτες */}
            <div className="mt-6 space-y-4 md:hidden" data-testid="board-cards">
              {rows.map((row, i) => (
                <div key={row.criterion} className="rounded-lg border border-line bg-paper p-5">
                  <p className="font-serif text-lg font-semibold text-ink">{row.criterion}</p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-terra">{row.source}</p>
                  <div className="mt-4 space-y-4">
                    {THEORIES.map((t) => {
                      const cell = row.cells[t.key];
                      return (
                        <div key={t.key} className="border-t border-line pt-3" data-testid={`board-mcell-${i}-${t.key}`}>
                          <p className="text-xs font-bold uppercase tracking-[0.12em] text-ink/60">
                            {t.name}
                          </p>
                          <div className="mt-1.5"><Pill score={cell.score} meta={SCORE_META} /></div>
                          <p className="mt-1.5 text-sm leading-relaxed text-foreground/85">{cell.note}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: πίνακας */}
            <div className="mt-6 hidden overflow-x-auto rounded-lg border border-line md:block" data-testid="board-table">
              <table className="w-full min-w-[880px] border-collapse text-left">
                <thead>
                  <tr className="text-paper">
                    <th className="w-[26%] bg-aegean px-5 py-4 text-xs font-bold uppercase tracking-[0.15em]">{ui.boardCriterionCol}</th>
                    {THEORIES.map((t) => (
                      <th key={t.key} className="bg-aegean px-5 py-4" data-testid={`board-head-${t.key}`}>
                        <span className="block text-sm font-bold text-paper">{t.name}</span>
                        <span className="block text-[11px] font-normal text-paper/80">{t.sub}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.criterion} className={i % 2 === 0 ? "bg-paper" : "bg-surface/60"}>
                      <td className="align-top px-5 py-5">
                        <p className="font-serif text-base md:text-lg font-semibold text-ink">{row.criterion}</p>
                        <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-terra">{row.source}</p>
                      </td>
                      {THEORIES.map((t) => {
                        const cell = row.cells[t.key];
                        return (
                          <td key={t.key} className="align-top px-5 py-5" data-testid={`board-cell-${i}-${t.key}`}>
                            <Pill score={cell.score} meta={SCORE_META} />
                            <p className="mt-2 text-sm leading-relaxed text-foreground/85">{cell.note}</p>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Σύγκριση των τριών νησιών — μετά τον πίνακα/τις κάρτες, μόνο εδώ εμφανίζονται οι κύκλοι */}
            <div className="mt-10" data-testid="board-comparison">
              <h3 className="font-serif text-lg font-semibold text-ink md:text-xl">{ui.boardCompareTitle}</h3>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{ui.boardWeightNote}</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {THEORIES.map((t) => {
                  const rs = RANK_STYLES[rankOf(t.key)];
                  const pct = maxTotal ? Math.round((100 * totals[t.key]) / maxTotal) : 0;
                  return (
                    <div key={t.key} className="rounded-lg border border-line bg-paper p-4" data-testid={`board-rank-${t.key}`}>
                      <p className="font-serif text-base font-semibold text-ink">{t.name}</p>
                      <p className="text-[11px] text-muted-foreground">{t.sub}</p>
                      <div
                        className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-line"
                        role="img"
                        aria-label={`${t.name}: ${pct}%`}
                      >
                        <div className="h-full rounded-full bg-ink transition-[width] duration-500" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="mt-2 text-xs font-semibold text-foreground/70">{ui[rs.labelKey]}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-8 flex justify-center">
              <button
                type="button"
                data-testid="board-back"
                onClick={() => { setBuilt(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="inline-flex items-center gap-2 rounded-full bg-aegean px-8 py-3.5 text-sm font-semibold text-paper transition-colors duration-200 hover:bg-ink"
              >
                <Undo2 className="h-4 w-4" />
                {ui.boardBack}
              </button>
            </div>
          </section>
        )}
      </div>
    </main>
  );
};
